import React, { useState, useCallback, useMemo } from "react";
import {
    Box,
    Drawer,
    Typography,
    IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import ConfirmationModal from "./ConfirmationModal";
import ReportForm from "./forms/ReportForm";
import {useSendEmail} from "../../../api/sendEmailService";

export default function ReportDrawer({ open, onClose }) {
    const { t } = useTranslation();
    const theme = useTheme();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [contactDetails, setContactDetails] = useState("");

    // Initialize the mutation hook
    const { mutate: sendEmail, isLoading, isError, error } = useSendEmail();

    // Handle form submission
    const handleFormSubmit = useCallback((formData) => {
        console.log("Report request submitted:", formData);
        const details = formData.contactMethod && formData.contactValue
            ? `${formData.contactMethod}: ${formData.contactValue}`
            : "";
        setContactDetails(details);

        const data = {
            userInfo: formData,
            type: "report",
        }

        sendEmail(data, {
            onSuccess: () => {
                setShowConfirmation(true);
                onClose();
                setTimeout(() => {
                    setShowConfirmation(false);
                }, 5000);
            },
            onError: (err) => {
                console.error("Submission error:", err.message);
            },
        });
    }, [onClose]);

    // Handle close confirmation
    const handleCloseConfirmation = useCallback(() => {
        setShowConfirmation(false);
    }, []);


    // Fade animation for the form
    const fadeVariants = {
        initial: { opacity: 0 },
        animate: { opacity: 1, transition: { duration: 0.3 } },
        exit: { opacity: 0, transition: { duration: 0.2 } },
    };

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                sx={{ 
                    "& .MuiDrawer-paper": {
                        width: { xs: "100%", sm: "500px" },
                        maxWidth: "100%",
                        background: theme.palette.background.paper,
                        borderLeft: `1px solid ${theme.palette.grey[200]}`,
                        boxShadow: `0 0 60px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.15)'}`,
                    }
                }}
            >
                <Box sx={{
                    px: { xs: 3, sm: 4 },
                    py: 4,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}>
                    {/* Header with close button */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 4,
                        }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                fontSize: { xs: '1.5rem', sm: '1.75rem' },
                            }}
                        >
                            {t("drawer.report.title")}
                        </Typography>
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: theme.palette.text.secondary,
                                "&:hover": {
                                    color: theme.palette.text.primary,
                                    backgroundColor: theme.palette.grey[100],
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Form */}
                    <AnimatePresence>
                        {open && (
                            <motion.div
                                variants={fadeVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                            >
                                <ReportForm onSubmit={handleFormSubmit} t={t} />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Box>
            </Drawer>

            {/* Confirmation Modal */}
            <AnimatePresence>
                {showConfirmation && (
                    <ConfirmationModal
                        open={showConfirmation}
                        onClose={handleCloseConfirmation}
                        title={t("drawer.report.confirmationTitle")}
                        message={t("drawer.report.confirmationMessage")}
                        confimation={t("drawer.report.confirmation")}
                        details={contactDetails}
                        detailsIcon={<ContactMailIcon sx={{ color: theme.palette.primary.main, fontSize: "1.5rem" }} />}
                        showDetails={!!contactDetails}
                    />
                )}
            </AnimatePresence>
        </>
    );
}